<template>
    <div>
        <div
            v-for="row in restrictions"
            :key="row.id"
            class="rounded-md border border-taupe-200 p-3 mb-2 space-y-2"
        >
            <div class="flex items-center gap-2">
                <zen-auto-complete
                    v-model="row.project_ulid"
                    class="flex-1"
                    :options="projects"
                    option-label="name"
                    option-value="ulid"
                    filter-label="name"
                    placeholder="Select a project..."
                    show-dropdown-button
                    :min-length="0"
                    @change="onProjectChange(row)"
                />
                <zen-button
                    variant="link-danger"
                    class="shrink-0"
                    @click="removeRestriction(row.id)"
                >
                    Remove
                </zen-button>
            </div>

            <div v-if="row.project_ulid" class="pl-1">
                <div class="flex items-center gap-4 text-sm text-taupe-700">
                    <label class="flex items-center gap-1.5 cursor-pointer">
                        <input
                            type="radio"
                            :name="'grant-mode-' + row.id"
                            value="all"
                            v-model="row.mode"
                            @change="emitUpdate"
                            class="h-4 w-4 border-taupe-300 text-sky-600 focus:ring-sky-500"
                        />
                        All environments
                    </label>
                    <label class="flex items-center gap-1.5 cursor-pointer">
                        <input
                            type="radio"
                            :name="'grant-mode-' + row.id"
                            value="specific"
                            v-model="row.mode"
                            @change="emitUpdate"
                            class="h-4 w-4 border-taupe-300 text-sky-600 focus:ring-sky-500"
                        />
                        Specific environments
                    </label>
                </div>

                <div
                    v-if="row.mode === 'specific'"
                    class="mt-2 flex flex-wrap gap-3"
                >
                    <label
                        v-for="env in environmentsForProject(row.project_ulid)"
                        :key="env.ulid"
                        class="flex items-center gap-1.5 text-sm text-taupe-700 cursor-pointer"
                    >
                        <input
                            type="checkbox"
                            :value="env.ulid"
                            v-model="row.environment_ulids"
                            @change="emitUpdate"
                            class="h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-500"
                        />
                        {{ env.name }}
                    </label>
                    <p
                        v-if="
                            environmentsForProject(row.project_ulid).length ===
                            0
                        "
                        class="text-xs text-taupe-500"
                    >
                        This project has no environments.
                    </p>
                </div>
            </div>
        </div>

        <zen-button variant="link" @click="addRestriction">
            + Add project restriction
        </zen-button>
    </div>
</template>

<script>
import {
    environmentsForProject,
    findProjectByEnvironmentUlid,
} from '@/utils/project-environments'

let nextRestrictionId = 0

export default {
    name: 'ResourceGrantsEditor',
    props: {
        modelValue: {
            type: Array,
            default: () => [],
        },
        projects: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            restrictions: [],
            // Grants that couldn't be mapped back to a project/environment
            // visible in `projects` (e.g. a deleted project). Preserved
            // as-is so editing unrelated scopes doesn't silently drop them.
            passthroughGrants: [],
        }
    },
    created() {
        this.parseModelValue(this.modelValue)
        // Round-trips the parsed grants back through the parent immediately,
        // so any invalid/orphaned entries in the initial value are
        // sanitized up front rather than only on the next user edit.
        this.emitUpdate()
    },
    methods: {
        environmentsForProject(projectUlid) {
            return environmentsForProject(this.projects, projectUlid)
        },
        findProjectByEnvironmentUlid(environmentUlid) {
            return findProjectByEnvironmentUlid(this.projects, environmentUlid)
        },
        parseModelValue(grants) {
            const restrictions = []
            const passthrough = []
            const rowByProjectUlid = new Map()

            for (const grant of grants ?? []) {
                if (grant.resource_type === 'project') {
                    restrictions.push({
                        id: ++nextRestrictionId,
                        project_ulid: grant.resource_ulid,
                        mode: 'all',
                        environment_ulids: [],
                    })
                    continue
                }

                if (grant.resource_type === 'environment') {
                    const project = this.findProjectByEnvironmentUlid(
                        grant.resource_ulid
                    )
                    if (project == null) {
                        passthrough.push(grant)
                        continue
                    }

                    let row = rowByProjectUlid.get(project.ulid)
                    if (row == null) {
                        row = {
                            id: ++nextRestrictionId,
                            project_ulid: project.ulid,
                            mode: 'specific',
                            environment_ulids: [],
                        }
                        rowByProjectUlid.set(project.ulid, row)
                        restrictions.push(row)
                    }
                    row.environment_ulids.push(grant.resource_ulid)
                    continue
                }

                passthrough.push(grant)
            }

            this.restrictions = restrictions
            this.passthroughGrants = passthrough
        },
        addRestriction() {
            this.restrictions.push({
                id: ++nextRestrictionId,
                project_ulid: '',
                mode: 'all',
                environment_ulids: [],
            })
        },
        removeRestriction(id) {
            this.restrictions = this.restrictions.filter((row) => row.id !== id)
            this.emitUpdate()
        },
        onProjectChange(row) {
            row.environment_ulids = []
            this.emitUpdate()
        },
        emitUpdate() {
            const grants = []
            for (const row of this.restrictions) {
                if (!row.project_ulid) continue

                if (row.mode === 'all') {
                    grants.push({
                        resource_type: 'project',
                        resource_ulid: row.project_ulid,
                    })
                } else {
                    for (const environmentUlid of row.environment_ulids) {
                        grants.push({
                            resource_type: 'environment',
                            resource_ulid: environmentUlid,
                        })
                    }
                }
            }
            this.$emit('update:modelValue', [
                ...grants,
                ...this.passthroughGrants,
            ])
        },
    },
}
</script>
